import { Link } from "react-router-dom";
import Layout from "../components/site/Layout";
import Seo from "../components/Seo";
import Converter from "../components/Converter";
import NativeAd from "../components/NativeAd";
import Faq from "../components/Faq";
import { track } from "../analytics";
import "./Home.css";

const AppleIcon = () => (
  <svg viewBox="0 0 384 512" width="24" height="24" fill="currentColor" aria-hidden="true">
    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
  </svg>
);

const PlayIcon = () => (
  <svg viewBox="0 0 512 512" width="22" height="22" fill="currentColor" aria-hidden="true">
    <path d="M48.5 17.7C46 22 44 27.5 44 34.8v442.3c0 6.4 1.6 11.7 4.5 15.8l248.3-248.3L48.5 17.7zM325.3 234.3 89.9 7.1C83.4 1 75.7 0 67.7 3.8L325.3 234.3zM456.5 222.7l-79.4-45.8-89.5 89.5 89.5 89.5 79.4-45.8c19.3-11.1 19.3-66.3 0-77.4zM67.7 508.2c8 3.8 15.7 2.8 22.2-3.3l235.4-227.2-67.4-67.4L67.7 508.2z" />
  </svg>
);

const STEPS = [
  { n: "1", t: "Upload your EPS", d: "Drag and drop your .eps file or browse to select it. Nothing is installed." },
  { n: "2", t: "Preview instantly", d: "We render the Encapsulated PostScript file and show you an accurate preview in seconds." },
  { n: "3", t: "Download PNG, JPG or PDF", d: "Save the result in the format you need — a PNG or JPG image, or a print-ready PDF." },
];

const FEATURES = [
  { icon: "🖼️", t: "View EPS online", d: "Open .eps files directly in your browser — no Illustrator, Photoshop or plugins required." },
  { icon: "🔄", t: "Convert to PNG, JPG & PDF", d: "Save your EPS as a PNG or JPG image, or a print-ready PDF — in one click." },
  { icon: "🎯", t: "Accurate rendering", d: "Crisp shapes, sharp lines and faithful colors from the original PostScript data." },
  { icon: "🔒", t: "Private & secure", d: "Files are processed over an encrypted connection and removed after preview." },
  { icon: "📱", t: "Works on any device", d: "Windows, macOS, Linux, Android or iOS — anything with a modern browser." },
  { icon: "⚡", t: "Free, no signup", d: "No account, no watermarks, no software downloads. Just open and convert." },
];

const FORMATS = [
  { to: "/guides/convert-eps-to-png", t: "EPS → PNG", d: "Best for transparent, shareable images." },
  { to: "/guides/convert-eps-to-jpg", t: "EPS → JPG", d: "Smaller files for photos and web." },
  { to: "/guides/convert-eps-to-pdf", t: "EPS → PDF", d: "Universal, print-ready document." },
];

const GUIDES = [
  { to: "/guides/what-is-an-eps-file", t: "What Is an EPS File?", d: "The format explained in plain English — history, uses, pros and cons." },
  { to: "/guides/how-to-open-eps-files", t: "How to Open EPS Files", d: "Every way to open EPS on Windows, Mac, Linux and online." },
  { to: "/guides/eps-vs-svg-vs-ai-vs-pdf", t: "EPS vs SVG vs AI vs PDF", d: "Which vector format to use — and when to convert." },
];

const FAQS = [
  { q: "What is an EPS file?", a: <>EPS (Encapsulated PostScript) is a vector graphics format used for logos, illustrations and print artwork. Learn more in our <Link to="/guides/what-is-an-eps-file">guide to EPS files</Link>.</> },
  { q: "Is the EPS Viewer free?", a: <>Yes. Viewing your EPS and downloading a PNG is completely free, with no account or watermark.</> },
  { q: "Do I need to install any software?", a: <>No. Everything runs in your browser. There is nothing to download or install.</> },
  { q: "Are my files safe?", a: <>Files are uploaded over an encrypted (HTTPS) connection and removed after your preview. We never share your artwork.</> },
  { q: "Can I open EPS files on my phone?", a: <>Yes. Because rendering happens on our servers, you only need a modern mobile browser to view and convert EPS files.</> },
  { q: "Why does my EPS look blurry?", a: <>Many EPS files embed a low-resolution thumbnail. Our viewer renders the true vector data. See <Link to="/guides/fix-eps-file-problems">common EPS problems</Link> for more.</> },
];

const jsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebApplication",
      name: "EPS Viewer",
      url: "https://epsviewer.in/",
      applicationCategory: "MultimediaApplication",
      operatingSystem: "Any (web-based)",
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
      description: "Free online EPS viewer and converter. Open, preview and convert Encapsulated PostScript files to PNG in your browser.",
    },
    {
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: typeof f.a === "string" ? f.a : f.q },
      })),
    },
  ],
};

export default function Home() {
  return (
    <Layout>
      <Seo
        title="EPS Viewer – Open & Convert EPS Files Online Free"
        description="Free online EPS viewer and converter. Open, preview and convert Encapsulated PostScript (.eps) files to PNG instantly in your browser — no software, no signup."
        path="/"
        jsonLd={jsonLd}
      />

      {/* HERO */}
      <section className="hero">
        <div className="container hero__grid">
          <div className="hero__copy">
            <span className="badge">Free · No signup · Secure</span>
            <h1 className="hero__title">Open &amp; convert <span className="grad">EPS files</span> online</h1>
            <p className="hero__lead">
              Upload an Encapsulated PostScript (<code>.eps</code>) file to preview it
              instantly and download it as a <strong>PNG, JPG or PDF</strong> — right in
              your browser. No Adobe Illustrator, no installs, no watermarks.
            </p>
            <ul className="hero__points">
              <li>✓ View EPS without design software</li>
              <li>✓ Convert EPS to PNG, JPG or PDF</li>
              <li>✓ Works on Windows, Mac, Linux &amp; mobile</li>
            </ul>
          </div>
          <div className="hero__tool">
            <Converter />
          </div>
        </div>
        <div className="hero__trust container">
          <span>🔒 Secure &amp; encrypted</span>
          <span>⚡ Instant preview</span>
          <span>🆓 100% free</span>
          <span>🌐 Any device</span>
        </div>
      </section>

      <NativeAd />

      {/* HOW IT WORKS */}
      <section className="section section--soft">
        <div className="container">
          <div className="section-head--center">
            <span className="eyebrow">How it works</span>
            <h2 className="section-title">Convert an EPS in three steps</h2>
            <p className="section-lead">No technical knowledge needed — if you can drag a file, you can use EPS Viewer.</p>
          </div>
          <div className="steps">
            {STEPS.map((s) => (
              <div className="step card" key={s.n}>
                <div className="step__n">{s.n}</div>
                <h3>{s.t}</h3>
                <p>{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW TO SAVE */}
      <section className="section">
        <div className="container">
          <div className="section-head--center">
            <span className="eyebrow">Saving your file</span>
            <h2 className="section-title">How to save an EPS as PNG, JPG or PDF</h2>
            <p className="section-lead">
              Open your file, then pick the format that fits where you&apos;ll use it.
              After the preview loads, the download buttons appear right under it.
            </p>
          </div>
          <div className="saveformats">
            <div className="saveformat card">
              <h3>Save as PNG</h3>
              <p>Best for logos and graphics that need a <strong>transparent background</strong>. Click <strong>PNG</strong> below the preview.</p>
              <Link to="/guides/convert-eps-to-png" className="format__link">PNG guide →</Link>
            </div>
            <div className="saveformat card">
              <h3>Save as JPG</h3>
              <p>Smaller files for photos and the web (a <strong>solid background</strong>). Click <strong>JPG</strong> to download.</p>
              <Link to="/guides/convert-eps-to-jpg" className="format__link">JPG guide →</Link>
            </div>
            <div className="saveformat card">
              <h3>Save as PDF</h3>
              <p>A <strong>universal, print-ready</strong> file that keeps the vectors sharp. Click <strong>PDF</strong> to download.</p>
              <Link to="/guides/convert-eps-to-pdf" className="format__link">PDF guide →</Link>
            </div>
          </div>
          <p className="saveformats__steps">
            In short: upload your <code>.eps</code> → wait for the preview → click
            <strong> PNG</strong>, <strong>JPG</strong> or <strong>PDF</strong> to save it.
          </p>
        </div>
      </section>

      {/* FEATURES */}
      <section className="section section--soft" id="features">
        <div className="container">
          <div className="section-head--center">
            <span className="eyebrow">Why EPS Viewer</span>
            <h2 className="section-title">Everything you need to work with EPS</h2>
          </div>
          <div className="features">
            {FEATURES.map((f) => (
              <div className="feature card" key={f.t}>
                <div className="feature__icon">{f.icon}</div>
                <h3>{f.t}</h3>
                <p>{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FORMATS */}
      <section className="section section--soft">
        <div className="container">
          <div className="section-head--center">
            <span className="eyebrow">Conversions</span>
            <h2 className="section-title">Convert EPS to the format you need</h2>
            <p className="section-lead">Download your EPS as PNG, JPG or PDF directly in the viewer. Read our guides for every common conversion.</p>
          </div>
          <div className="formats">
            {FORMATS.map((f) => (
              <Link className="format card" to={f.to} key={f.t}>
                <h3>{f.t}</h3>
                <p>{f.d}</p>
                <span className="format__link">Read guide →</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT EPS (content) */}
      <section className="section">
        <div className="container about-eps">
          <div>
            <span className="eyebrow">About the format</span>
            <h2 className="section-title">What is an EPS file?</h2>
            <p>
              EPS stands for <strong>Encapsulated PostScript</strong> — a vector
              graphics format created by Adobe in the late 1980s. Because it stores
              artwork as mathematical paths rather than pixels, an EPS file can be
              scaled to any size, from a business card to a billboard, without ever
              losing quality.
            </p>
            <p>
              EPS is still widely used by printers, sign makers and design agencies
              for logos and high-resolution artwork. Its main drawback is that you
              normally need professional software to open it — which is exactly the
              problem EPS Viewer solves. Upload your file and see it instantly, then
              convert it to a format you can use everywhere.
            </p>
            <p>
              <Link to="/guides/what-is-an-eps-file" className="btn btn--ghost">Read the full guide →</Link>
            </p>
          </div>
          <ul className="about-eps__facts">
            <li><strong>Type:</strong> Vector (can embed raster)</li>
            <li><strong>Extension:</strong> .eps</li>
            <li><strong>Created by:</strong> Adobe Systems</li>
            <li><strong>Best for:</strong> Logos, print &amp; signage</li>
            <li><strong>Opens with:</strong> Illustrator, Inkscape, or EPS Viewer</li>
          </ul>
        </div>
      </section>

      {/* GUIDES */}
      <section className="section section--soft">
        <div className="container">
          <div className="section-head--center">
            <span className="eyebrow">Guides &amp; tutorials</span>
            <h2 className="section-title">Learn everything about EPS files</h2>
          </div>
          <div className="guides">
            {GUIDES.map((g) => (
              <Link className="guide card" to={g.to} key={g.t}>
                <h3>{g.t}</h3>
                <p>{g.d}</p>
                <span className="format__link">Read →</span>
              </Link>
            ))}
          </div>
          <div className="section-head--center" style={{ marginTop: 28 }}>
            <Link to="/guides" className="btn btn--ghost">Browse all guides</Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section">
        <div className="container">
          <div className="section-head--center">
            <span className="eyebrow">FAQ</span>
            <h2 className="section-title">Frequently asked questions</h2>
          </div>
          <Faq items={FAQS} />
        </div>
      </section>

      {/* MOBILE APPS */}
      <section className="section section--soft">
        <div className="container">
          <div className="section-head--center">
            <span className="eyebrow">Mobile apps</span>
            <h2 className="section-title">Try EPS Viewer on iOS &amp; Android</h2>
            <p className="section-lead">
              Prefer an app? View and convert EPS files on the go — free on the
              App Store and Google Play.
            </p>
          </div>
          <div className="store-badges">
            <a
              className="store-badge"
              href="https://apps.apple.com/us/app/eps-viewer/id6756544460"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track("btnPress_appStore")}
            >
              <AppleIcon />
              <span className="store-badge__txt"><small>Download on the</small><strong>App Store</strong></span>
            </a>
            <a
              className="store-badge"
              href="https://play.google.com/store/apps/details?id=com.viewer.eps"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track("btnPress_playStore")}
            >
              <PlayIcon />
              <span className="store-badge__txt"><small>Get it on</small><strong>Google Play</strong></span>
            </a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-band">
        <div className="container">
          <h2>Ready to open your EPS file?</h2>
          <p>Upload your file and get an instant preview — free, secure and right in your browser.</p>
          <a href="#main" className="btn btn--primary btn--lg">Open the EPS Viewer</a>
        </div>
      </section>
    </Layout>
  );
}
